import useCancelInputPutMutation from "@/api/input/useCancelInputPutMutation";
import { workLogs } from "@/api/workLogs/useWorksLogsQuery";
import { ItemsDetailForm } from "@/features/item/components/form/ItemsDetailForm";
import { LocationDetailForm } from "@/features/location/detail/LocationDetailForm";
import { useModal } from "@/features/modal/ModalStackManager";
import { DetailLink } from "@/features/ui/detail/DetailLink";
import { theme } from "@/styles/theme";
import { setDateFormatStringWithTime } from "@/utils/dateTimeUtil";
import { setToLocaleString } from "@/utils/unitMark";
import { setWorkStatus } from "@/utils/workStatus";
import styled from "@emotion/styled";
import { Badge, Button, Flex, Pagination, Table, Text } from "@mantine/core";
import {
  WorkLogsGet200ResponseRowsInner,
  WorkLogsGet200ResponseRowsInnerWorkLogTypeEnum,
} from "@sizlcorp/sizl-api-document/dist/models";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { WorkViewProps } from "./WorkView";

export const WorkProductionHistory = ({ workId }: WorkViewProps) => {
  const [activePage, setActivePage] = useState<number>(1);
  const { t } = useTranslation();

  // workId?: number
  const { mutate: cancelInputMutate } = useCancelInputPutMutation("cancel");

  const { data: workLogsData } = useQuery(
    workLogs.get({
      query: {
        $and: [
          { workId: { $eq: workId ?? 0 } },
          {
            $and: [
              {
                workLogType: {
                  $ne: WorkLogsGet200ResponseRowsInnerWorkLogTypeEnum.TIME_TRACKING_START,
                },
              },
              {
                workLogType: {
                  $ne: WorkLogsGet200ResponseRowsInnerWorkLogTypeEnum.TIME_TRACKING_END,
                },
              },
              {
                workLogType: {
                  $ne: WorkLogsGet200ResponseRowsInnerWorkLogTypeEnum.TIME_TRACKING_PAUSE,
                },
              },
              {
                workLogType: {
                  $ne: WorkLogsGet200ResponseRowsInnerWorkLogTypeEnum.TIME_TRACKING_RESUME,
                },
              },
            ],
          },
        ],
      },
      sort: "-id",
      page: activePage,
      populate: [
        "itemUnit",
        "defectName",
        "alreadyCanceled",
        "creatorUser",
        "downtimeReasonName",
        "getEquipment",
        "workerData",
      ],
    }),
  );

  const rows: readonly WorkLogsGet200ResponseRowsInner[] = workLogsData?.data.rows ?? [];

  const workLogTypeColor = {
    INPUT: "blue.7",
    CONSUME: "pink.7",
    CONSUME_IDENTIFIED: "royalblue.7",
    DEFECT: "red.7",
    REWORK: "yellow.7",
    START: "green.7",
    END: "purple.7",
    LOSS: "lime.7",
    PRODUCTION: "gray.7",
    CANCEL_INPUT: "red.4",
    CANCEL_CONSUME_IDENTIFIED: "royalblue.4",
  };

  const onCancelInput = ({ inputLotId }: { inputLotId: number }) => {
    window.confirm("투입을 취소하시겠습니까?") &&
      cancelInputMutate(
        {
          workLogId: inputLotId,
        },
        {
          onError: (e: any) => {
            if (e.response?.data?.message) {
              alert(e.response?.data?.message);
            }
          },
        },
      );
  };

  const { openModal } = useModal();

  return (
    <WorkTabBodyContainer>
      <TabBodyTitle>
        <Text fz="xl" fw={500}>
          {t("productionHistory.productionHistory")}
        </Text>
        <Text fz="xs" fw={400}>
          {t("productionHistory.description")}
        </Text>
      </TabBodyTitle>
      <TabBodyTable>
        <Thead>
          <Tr>
            <Th width={80}>{t("workLog.id")}</Th>
            <Th width={120}>{t("workLog.createdAt")}</Th>
            <Th width={90}>{t("workLog.type")}</Th>
            <Th width={110}>{t("inventory.quantity")}</Th>
            <Th width={180}>{t("productionHistory.bomItem")}</Th>
            <Th width={180}>{t("productionHistory.bomLot")}</Th>
            <Th width={100}>{t("work.fromLocation")}</Th>
            <Th width={100}>{t("productionHistory.logNum")}</Th>
            <Th width={100}>{t("workLog.worker")}</Th>
            <Th>{t("common.action")}</Th>
          </Tr>
        </Thead>
        <Tbody>
          {rows.length ? (
            rows.map((workLog) => (
              <Tr>
                <Td width={80} style={{ justifyContent: "flex-end" }}>
                  {workLog.id}
                </Td>
                <Td width={120}>{setDateFormatStringWithTime(workLog.createdAt)}</Td>
                <Td width={90}>
                  <Badge
                    color={workLogTypeColor[workLog.workLogType as keyof typeof workLogTypeColor]}
                  >
                    {t(setWorkStatus(workLog.workLogType))}
                  </Badge>
                </Td>
                <Td width={110} style={{ justifyContent: "flex-end" }}>
                  {setToLocaleString(workLog.quantity)} {workLog?.unitText}
                </Td>
                <Td width={180}>
                  <DetailLink
                    onClick={() =>
                      openModal(
                        <ItemsDetailForm
                          itemCode={
                            workLog.logData?.incoming?.itemCode ??
                            workLog.logData?.outgoing?.itemCode
                          }
                        />,
                        null,
                        "",
                      )
                    }
                  >
                    {workLog.logData
                      ? workLog.logData?.incoming
                        ? workLog.logData?.incoming?.itemData?.name
                        : workLog.logData?.outgoing?.itemData?.name
                      : ""}
                  </DetailLink>
                </Td>
                <Td width={180}>
                  {workLog.logData ? (
                    workLog.logData?.incoming ? (
                      <Flex direction="column">
                        <Text>id: {workLog.logData?.incoming?.lotData?.id}</Text>
                        <Text>
                          {t("lot.name")} : {workLog.logData?.incoming?.lotData?.name}
                        </Text>
                        <Text>
                          {t("lot.expiration")} : {workLog.logData?.incoming?.lotData?.expiration}
                        </Text>
                      </Flex>
                    ) : (
                      <Flex direction="column">
                        <Text>id: {workLog.logData?.outgoing?.lotData?.id}</Text>
                        <Text>
                          {t("lot.name")} : {workLog.logData?.outgoing?.lotData?.name}
                        </Text>
                        <Text>
                          {t("lot.expiration")} : {workLog.logData?.outgoing?.lotData?.expiration}
                        </Text>
                      </Flex>
                    )
                  ) : null}
                </Td>
                <Td width={80}>
                  <DetailLink
                    onClick={() =>
                      openModal(
                        <LocationDetailForm
                          locationCode={
                            workLog.logData?.incoming?.locationCode ??
                            workLog.logData?.outgoing?.locationCode
                          }
                        />,
                        null,
                        "",
                      )
                    }
                  >
                    {workLog.logData
                      ? workLog.logData?.incoming
                        ? workLog.logData?.incoming.locationData?.name
                        : workLog.logData?.outgoing?.locationData?.name
                      : ""}
                  </DetailLink>
                </Td>
                <Td width={120} style={{ justifyContent: "flex-end" }}>
                  {workLog.parentWorkLogId}
                </Td>
                <Td>
                  {/* <DetailLink
                    onClick={() =>
                      openModal(<UsersDetailForm UserCode={workLog.workerName?.code} />, null, "")
                    }
                  > */}
                  {workLog.workerName?.map((value) => value.name).join(", ")}
                  {/* </DetailLink> */}
                </Td>
                <Td style={{ justifyContent: "center", alignItems: "center" }}>
                  {workLog.workLogType === WorkLogsGet200ResponseRowsInnerWorkLogTypeEnum.INPUT && (
                    <Button
                      disabled={workLog.alreadyCanceled}
                      styles={{
                        root: {
                          "&:hover": {
                            backgroundColor: theme.colors?.yellow?.[5],
                          },
                        },
                      }}
                      onClick={() => onCancelInput({ inputLotId: workLog.id as number })}
                      size="xs"
                      bg={theme.colors?.yellow?.[6]}
                    >
                      {workLog.alreadyCanceled
                        ? t("productionHistory.cancelled")
                        : t("productionHistory.cancel")}
                    </Button>
                  )}
                </Td>
              </Tr>
            ))
          ) : (
            <NoDataTr>
              <Text>{t("productionHistory.notFound")}</Text>
            </NoDataTr>
          )}
        </Tbody>
      </TabBodyTable>
      <PaginationWrapper>
        <Pagination
          onChange={setActivePage}
          value={activePage}
          total={workLogsData?.data?.totalPages ?? 0}
          size="lg"
          radius="sm"
        />
      </PaginationWrapper>
    </WorkTabBodyContainer>
  );
};

export const WorkTabBodyContainer = styled.div`
  display: flex;
  padding: 0 32px 16px 32px;
  flex-direction: column;
  align-items: flex-start;
  flex: 1 0 0;
  align-self: stretch;
  gap: 0.5rem;
  overflow: auto;
  max-height: 40rem;
  background-color: white;
`;

export const TabBodyTitle = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  align-self: stretch;
`;

export const TabBodyTable = styled(Table)`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  align-self: stretch;
  border-collapse: separate;
  border-spacing: 0;
`;

export const Thead = styled.thead`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  align-self: stretch;
  position: sticky;
  top: 0;
  background: white;
  z-index: 10;
`;

export const Tr = styled.tr`
  display: flex;
  align-items: flex-start;
  align-self: stretch;
`;

export const Th = styled.th<{ width?: number }>`
  display: flex;
  width: ${(props) => (props.width ? `${props.width}px` : `auto`)};
  flex: ${(props) => (props.width ? `0 0 auto` : `1 0 0`)};
  padding: 7px 10px;
  align-items: flex-start;
  gap: 10px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const Tbody = styled.tbody`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  align-self: stretch;
`;

export const Td = styled.td<{ width?: number; direction?: string }>`
  display: flex;
  width: ${(props) => (props.width ? `${props.width}px` : `auto`)};
  flex: ${(props) => (props.width ? `0 0 auto` : `1 0 0`)};
  padding: 7px 10px;
  align-items: flex-start;
  gap: 10px;
  word-break: break-word;
  flex-direction: ${(props) => (props.direction ? props.direction : "")};
`;

const Content = styled.div`
  display: flex;
  padding: 2px 0px;
  flex-direction: column;
  justify-content: center;
  align-self: stretch;
  align-items: flex-start;
`;

const Divider = styled.div`
  height: 1px;
  align-self: stretch;
  background: ${theme.colors?.gray?.[3]};
`;

// const LeftSpace = styled.span`
//   width: 32px;
//   align-self: stretch;
//   background: ${theme.colors?.gray?.[3]};
//   border-bottom: ${theme.colors?.gray?.[3]} solid 1px;
//   opacity: 0.2;
// `;

// const RightSpace = styled.span`
//   flex: 1 0 0;
//   align-self: stretch;
//   background: ${theme.colors?.gray?.[3]};
//   border-bottom: ${theme.colors?.gray?.[3]} solid 1px;
//   opacity: 0.2;
// `;

export const PaginationWrapper = styled.div`
  display: flex;
  width: 100%;
  justify-content: center;
`;

export const NoDataTr = styled.tr`
  display: flex;
  width: 100%;
  height: 4rem;
  justify-content: center;
  align-items: center;
`;
